/**
 * วิธีทำงานรากเทียม — the staff guideline on /reports/implant?tab=guide.
 *
 * One drawn map, top to bottom: how a patient goes from a missing tooth to a paid
 * implant, and back when the next tooth comes out. Written for the people doing the
 * work, in the words on their screens. Change the RULE in its own source, then the
 * words here:
 *   implant/workflow.md           the new-lead path (แอดมิน → Sale), 5 Sep 2026
 *   implant/report.md §6d–§6h     review, call counting, rounds, W, parking — Ram's
 *                                 decisions to 29 Oct, built as phases 1–5
 *   implant/recall-journey.html   the same journey, drawn for Ram
 *
 * Rewritten 2026-10-30 against the three working tabs รีวิว → รอโทร → ผลการโทร: รอโทร as
 * one list of every type in api 678's order, ผลการโทร as the log book (api 680), the
 * no-call-today check, the 3-day wait after ไม่รับสาย and the live ประวัติการโทร (trigger 15);
 * later that day the cold step L · ปรึกษาเกิน 6 เดือน (api 678, consults 181-365 days).
 * Everything it describes is on screen; nothing here is a plan.
 *
 * Chip, list, view and review labels are NOT written here: the page reads them from
 * data/implantOutcomes.js, lib/implant.js and lib/implantReview.js, so they always
 * match the buttons staff press. src/lib/implantGuide.test.mjs checks the ids this
 * file does hold (hold appointment types, chip codes, list ids, jumps) against them.
 *
 * `**word**` in any string renders bold. `to` is a step key; the page turns it into
 * "→ ข้อ N ชื่อขั้น" and scrolls there on click.
 *
 * Staff-facing words, deliberately: "เลือกผลการโทร", never "chip"; the outcome's Thai
 * name, never NO / OC; "รายละเอียดนัดหมาย" (appointment.details — what tab 2 and the W
 * rows read), never "remark", which the booking form labels หมายเหตุภายใน.
 */

export const GUIDE_UPDATED = '2026-10-30'

// `color` = the role's own colour (kit Badge `color`), not a status tone: a role is not
// "done" or "a problem", so the tone palette would say the wrong thing.
export const ROLES = {
  admin:  { label: 'แอดมิน Chat/โทร', color: '#7c3aed', does: 'ลงนัด Implant · ปรึกษา · ขออนุญาตให้ Sale โทรกลับ · เปิด Ticket แท็ก Implant' },
  sale:   { label: 'Sale รากเทียม', color: '#d97706', does: 'โทรหา lead ทุกคนก่อนวันนัด · ตาม Ticket แท็ก Implant · โทรตามลิสต์ร่วมกับทีม' },
  team:   { label: 'ทีมโทร (ใครก็ได้)', color: '#0d9488', does: 'รีวิว 2 ขั้น · โทรตามลิสต์ · เลือกผลการโทรทุกสาย — คิวกลางคิวเดียว ไม่มีเจ้าของรายชื่อ' },
  branch: { label: 'หมอ & หน้าสาขา', color: '#2563eb', does: 'แนะนำการทดแทนฟันทุกเคส · ลงนัดขั้นต่อไปด้วยประเภทนัดรากเทียม · เขียนรายละเอียดนัดหมาย · บิลรากเทียมผ่านโปรโมชั่นรากเทียม' },
  system: { label: 'ระบบทำให้', color: '#64748b', does: 'สร้างลิสต์ทุกเช้า 07:05 · ซ่อนคนที่ไม่ควรโทร · เรียงลำดับรอโทร · ย้ายคนที่ยังไม่ต้องโทรไปฝั่ง ไม่ต้องโทรตอนนี้ · เก็บผลการโทรลงสมุดบันทึก' },
}

/** The one rule everything else follows (Ram, 2026-10-28). */
export const THE_RULE = [
  'รอบหนึ่ง **เริ่ม** เมื่อคนไข้ถอนฟันที่คลินิก หรือมีนัด **Implant · ปรึกษา**',
  'และ **จบ** เมื่อจ่ายเงินค่ารากเทียม ฟันปลอม หรือสะพานฟันเท่านั้น',
  'ระหว่างนั้น **สมุดนัด** เป็นตัวบอกว่าต้องโทรใคร — ผลการโทรที่เลือกเป็นแค่บันทึกว่าได้ยินอะไรมา',
]

/**
 * The flowchart's boxes, top to bottom. Numbers come from this order; `to` keys point here.
 *
 * A box shows only `title` + `summary` until it is clicked (Ram, 2026-10-29: "let user
 * click then have more detail explanation"). The click opens the box's detail (the
 * per-step blocks below) and `why` — the reason behind the rule, so staff can apply it
 * to a case the guide does not list. Every `why` is a measured fact or a decision
 * recorded in implant/report.md or project.md; none is a guess.
 */
export const STEPS = [
  {
    key: 'doors', title: 'ทางเข้า', who: ['admin', 'sale', 'branch', 'system'],
    summary: 'คนไข้เข้ามาได้ 3 ทาง: ทักมาใหม่ · ถอนฟันที่คลินิก · คุยที่สาขา',
    why: [
      'แท็กใน OHO ไม่เข้า CRM — ไม่เปิด Ticket ระบบก็ไม่รู้ว่ามี lead คนนี้ และนับยอด chat รากเทียมไม่ได้',
      'นัดปรึกษารากเทียมที่บันทึกไว้ลดลง 142 → 93 → 70 (ไตรมาสแรกปี 2024 → 2026) ทั้งที่รายได้รากเทียมเพิ่มขึ้น — คนไข้ไม่ได้หายไป แต่คุยกันในนัดอื่นแล้วไม่ได้ลงนัด **Implant · ปรึกษา**',
    ],
  },
  {
    key: 'who', title: 'ใครอยู่ในลิสต์', who: ['system'],
    summary: 'ระบบคัดให้ทุกเช้า — ใครควรโทร ใครรอไว้ก่อน ใครไม่ต้องโทรเลย',
    why: [
      'ฟันคุด ฟันน้ำนม และฟันที่ถอนเพื่อจัดฟัน ไม่ใช่ช่องว่างที่ต้องทดแทน — โทรไปเสียเวลาทั้งสองฝ่าย',
      '**ยกเลิกเคส** คือสวิตช์ห้ามติดต่อตัวเดียวของคลินิก ใช้ร่วมกับ recall ปกติ — ปุ่มผลการโทร **ยกเลิกเคส** จึงเปลี่ยนสถานะคนไข้ให้ ไม่ใช่ป้ายแยกของหน้านี้',
      'ลิสต์คำนวณใหม่ทุกเช้าจากบิลจริง คนจึงย้ายลิสต์เองตามเวลา (G → C → D → F) ไม่ต้องย้ายเอง',
    ],
  },
  {
    key: 'pick', title: 'เลือกลิสต์', who: ['team'],
    summary: 'รอโทรรวมทุกประเภท ระบบเรียงให้ · ทำทีละประเภท: W → A → G → C → E → D → F → L',
    why: [
      '**W** มาปรึกษาแล้ว รู้แผนและราคาแล้ว · **A** จะเข้ามาที่คลินิกอยู่แล้ว · **G, C** ถอนมาไม่เกิน 6 เดือน เป็นจังหวะที่คุยเรื่องทดแทนได้ · **E** เป็นเคสที่ใหญ่ที่สุด',
      'หน้า ติดตาม › Recall ไม่มีปุ่มผลการโทร — โทรลิสต์ B จากหน้านั้น ระบบไม่รู้ว่าโทรแล้วได้ผลอะไร',
    ],
  },
  {
    key: 'review', title: 'รีวิวก่อนโทร', who: ['team'],
    summary: 'ดูชาร์ต 2 ขั้น (คัดกรอง → ตรวจซ้ำ) ก่อนโทร · ลิสต์ W ไม่ต้องรีวิว',
    why: [
      'ดูชาร์ตก่อน เพื่อไม่โทรเสนอรากเทียมให้คนที่ไม่ควรได้รับสาย เช่น ถอนฟันคุด หรือทดแทนไปแล้ว',
      'รีวิวเก็บแยกจากโน้ต Recall เพราะโน้ต Recall ทุกอัน ระบบถือว่า “โทรแล้ว” — คนไข้หลุดจากลิสต์ B ทั้งที่ยังไม่มีใครโทร · หน้าต่างคนไข้ในแท็บรีวิวจึงดูโน้ต Recall ได้อย่างเดียว',
    ],
  },
  {
    key: 'call', title: 'โทร', who: ['team', 'sale'],
    summary: 'จองสาย → ยืนยันตัวตน → คุย → เลือกผลการโทร 1 ปุ่ม → ขึ้นแท็บผลการโทร',
    why: [
      'ผลการโทรคือสิ่งเดียวที่ระบบอ่านได้ — ใช้ตัดสินว่าจะพักคนไข้ 6 เดือน พักจนมีรอบใหม่ หรือให้โทรต่อ และใช้นับผลงานของคนโทร',
      'ยืนยันตัวตนก่อน เพราะเรื่องการรักษาเป็นข้อมูลส่วนตัว (PDPA) — คนรับสายอาจไม่ใช่เจ้าตัว',
      'จองสายเพราะคนไข้คนเดียวอาจอยู่ทั้งในลิสต์รากเทียมและ recall ปกติ — ป้าย กำลังติดตาม กันไม่ให้สองแผนกโทรซ้ำ',
      'ผลการโทรที่ไม่มีสายจริงในวันเดียวกันถือว่าผิดปกติ (คุณหมอราม 29 ต.ค. 2026) — ระบบจึงเตือนก่อนบันทึก แต่ยังให้บันทึกต่อได้ เพราะบางสายโทรจากเบอร์อื่น',
    ],
  },
  {
    key: 'hold', title: 'จองแล้ว · รอวันนัด', who: ['branch', 'system'],
    summary: 'ลงนัดด้วยประเภทนัดรากเทียม แล้วระบบพักคนไข้ไว้จนถึงวันนัด',
    why: [
      'ระบบดูสมุดนัดเพื่อตัดสินว่าต้องโทรใคร — นัดที่ลงเป็น GP หรือ “อื่นๆ” ระบบไม่รู้ว่าเป็นงานรากเทียม คนไข้ที่นัดแล้วจึงถูกโทรซ้ำ',
      '**นัดได้** นับเฉพาะนัดประเภทรากเทียมที่ลงภายใน 14 วันหลังโทร — ลงผิดประเภท ผลงานของคนโทรหายไป',
    ],
  },
  {
    key: 'visit', title: 'วันนัด', who: ['branch'],
    summary: 'มา + จ่าย = จบ · ไม่จ่ายหรือไม่มา = กลับมาให้โทรวันรุ่งขึ้น',
    why: [
      'ทีมโทรเห็นรายละเอียดนัดหมายในแถวลิสต์ W — หมอไม่เขียน คนโทรก็ไม่รู้ว่าหมอเสนออะไรไว้',
      '82% ของคนที่ตัดสินใจทำ ใช้เวลานานกว่า 2 สัปดาห์ — จึงตามที่วันที่ 21 · 45 · 90 และนับว่าหลุดเมื่อครบ 180 วันเท่านั้น',
    ],
  },
  {
    key: 'done', title: 'จบรอบ', who: ['branch', 'system'],
    summary: 'จบเมื่อจ่ายค่ารากเทียม ฟันปลอม หรือสะพานฟันเท่านั้น',
    why: [
      'Stent ปลูกกระดูก ยกไซนัส CT และ Digital Guide เป็นขั้นก่อนฝังราก — จ่ายแล้วคนไข้ก็ยังไม่มีฟัน จึงยังต้องตามต่อ',
      'จ่ายงวดแรกก็นับว่าจบ เพราะคนไข้ตัดสินใจแล้ว — ไม่ต้องโทรขายซ้ำ',
    ],
  },
  {
    key: 'next', title: 'รอบใหม่', who: ['system'],
    summary: 'ถอนซี่ใหม่หรือปรึกษาใหม่ = เริ่มรอบใหม่ · แต่ละแบบกลับมาเมื่อไร',
    why: [
      'คนที่เคยทำรากเทียมแล้วถอนซี่ใหม่ คือกลุ่มที่อาจทำซี่ที่สอง — แต่เดิมระบบซ่อนเขาถาวร',
      'ผลรีวิวและผลการโทรผูกกับรอบ — “เคสไม่เหมาะ” เมื่อปีก่อน ไม่ได้แปลว่าไม่เหมาะกับฟันซี่ใหม่',
    ],
  },
]

/* ── ข้อ 1 · three ways in ─────────────────────────────────────────────────── */
export const DOORS = [
  {
    key: 'lead',
    title: 'คนไข้ใหม่ ทัก Chat / โทรเข้า',
    who: ['admin', 'sale'],
    do: [
      '**นัดได้** → ลงนัด **Implant · ปรึกษา** ทันที ใส่ช่องทางที่มา และรายละเอียดนัดหมาย',
      '**ยังไม่นัด** → ถามว่าสะดวกให้ Sale โทรกลับไหม → สะดวก = **เปิด Ticket แท็ก Implant** (ติดตาม › Ticket)',
      'ยังไม่มี HN → เปิด Ticket แบบ **ไม่ระบุคนไข้** เขียนชื่อ เบอร์ และสิ่งที่ถาม',
      'Sale โทรแนะนำตัว **ทุกคน** ก่อนวันนัด · บันทึกสิ่งที่คุยใน Ticket (ยังไม่นัด) หรือรายละเอียดนัดหมาย (นัดแล้ว)',
    ],
    warn: 'แท็กใน OHO ไม่เข้าระบบ — ไม่มี Ticket = ไม่มีใครตาม',
    to: 'hold',
  },
  {
    key: 'ext',
    title: 'ถอนฟันที่คลินิก',
    who: ['system'],
    do: [
      'ทุกเช้า **07:05** ระบบสร้างลิสต์ใหม่ — ถอนเมื่อวาน เช้านี้อยู่ในลิสต์',
      'จัดลิสต์ให้ตามเวลาที่ถอน และตามว่ามีนัดอยู่แล้วหรือไม่',
    ],
    to: 'who',
  },
  {
    key: 'chair',
    title: 'คนไข้อยู่ที่สาขาแล้ว',
    who: ['branch'],
    do: [
      'มีช่องว่าง → **แนะนำการทดแทนฟันทุกครั้ง**: รากเทียม หรือ ฟันปลอม',
      'สนใจ → **ลงนัดขั้นต่อไปก่อนคนไข้กลับ** ด้วยประเภทนัดรากเทียม',
      'ปรึกษาในนัด **Implant · ปรึกษา** แล้วยังไม่ซื้อ → วันรุ่งขึ้นขึ้นลิสต์ **W · Warm Case** ให้ทีมโทรตาม',
    ],
    warn: 'คุยเรื่องรากเทียมในนัด GP แต่ไม่ได้ลงนัด Implant · ปรึกษา = ระบบไม่เห็น ไม่มีใครตาม',
    to: 'hold',
  },
]

/* ── ข้อ 2 · who is in, who waits, who is out (the system does this) ─────────── */
export const LIST_IN = [
  'ถอนฟันทั่วไป **รวมสิทธิ์ ปกส.** — ฟันคุดที่ขึ้นแล้วก็อยู่ แต่มีป้าย **ซี่ 8**',
  'ผ่าฟันฝัง **ซี่อื่นที่ไม่ใช่ฟันคุด** (เช่น เขี้ยวฝัง)',
  'เคยทำรากเทียมซี่อื่นมาก่อน แล้ว **ถอนซี่ใหม่**',
  'ใส่ฟันปลอมถอดได้ และยังไม่ได้ทำรากเทียมหลังใส่ครั้งล่าสุด → ลิสต์ E',
  'มาปรึกษารากเทียมแล้วยังไม่ซื้อ ภายใน 180 วัน → ลิสต์ W',
  'ปรึกษาเมื่อ 181–365 วันก่อน ยังไม่ซื้อ ไม่มีนัด และไม่อยู่ในลิสต์ถอนฟัน → ลิสต์ L (ปรึกษาเกิน 6 เดือน) · หลัง 1 ปีหยุดโทร จนกว่าจะมีรอบใหม่',
  'สถานะ “รากเทียม” ที่ยังไม่เคยมีใครโทร → ลิสต์ B',
]

// The middle column is lib/implant.js VIEWS (รอวันนัด · พักไว้ · พักจนกว่าจะมีรอบใหม่),
// read by the page — not repeated here.
export const VIEWS_NOTE = 'อยู่ในแท็บ **รีวิว** ฝั่ง **ไม่ต้องโทรตอนนี้** — เปิดดูได้ ไม่จองสาย'

export const LIST_OUT = [
  'สถานะ **ยกเลิกเคส** — ไม่โทรทั้ง recall ปกติและรากเทียม',
  'จ่ายค่ารากเทียม ฟันปลอม หรือสะพานฟัน **หลังถอนซี่ล่าสุด** แล้ว',
  'จ่ายค่าปลูกกระดูก / ยกไซนัส — ซ่อน 3 เดือน แล้วกลับมาเอง',
  'ผ่าฟันคุด (ซี่ 8) · ถอนฟันน้ำนม · ถอนฟันสิทธิ์ **สปสช.**',
  'ถอนเพื่อจัดฟัน (3 เดือนก่อน – 6 เดือนหลังเริ่มโปรจัดฟัน) · **กำลังจัดฟันอยู่** — กลับมาเองเมื่อจัดฟันจบ',
  'ถอนนานเกิน 2 ปี',
]

/* ── ข้อ 3 · pick a list ───────────────────────────────────────────────────── */
// Ids from lib/implant.js PRESETS, in the order to work them (report.md §3.3 + §6g: W first).
export const LIST_ORDER = ['W', 'A', 'G', 'C', 'E', 'D', 'F', 'L']
export const PICK = {
  do: [
    'แท็บ **รอโทร** แสดง **ทุกประเภทรวมกัน** — คอลัมน์ **ประเภท** บอกว่าแต่ละคนมาจากลิสต์ไหน',
    'ระบบเรียงให้แล้ว โทรจากบนลงล่าง: **นัดหลุด** → **ปรึกษาแล้ว ยังไม่จ่าย** → **ปลูกกระดูกแล้ว รอฝังราก** → **กลับจากพัก** / **ดึงกลับมาโทร** → **โทรซ้ำ** → ที่เหลือ (มีนัดใกล้ก่อน · อายุ 55+ ก่อน · ถอนล่าสุดก่อน) → **ปรึกษาเกิน 6 เดือน** ท้ายสุด',
    'อยากทำทีละประเภท → เลือกในตัวกรอง **ประเภท** ตามลำดับ **W → A → G → C → E** ก่อน แล้วจึง D → F → L',
    '**W · Warm Case** — ปรึกษาภายใน 6 เดือน · โทรได้เลย ไม่ต้องรีวิว · แถวแสดงหมอ ข้อเสนอ และรายละเอียดนัด',
    '**L · ปรึกษาเกิน 6 เดือน** — ปรึกษาเมื่อ 6–12 เดือนก่อน ยังไม่จ่าย ไม่มีนัด และไม่อยู่ในลิสต์ถอนฟัน · ขั้นที่เย็นกว่า Warm Case ไม่ต้องรีวิว · ผลที่เคยได้ตอนเป็น Warm Case ยังมีผล (พักไว้ · ไปทำที่อื่นแล้ว)',
    '**A มีนัดอยู่แล้ว** (นัดอื่นที่ไม่ใช่งานรากเทียม เช่น ขูดหินปูน) — คุยก่อน หรือในวันที่เขาจะมา',
    '**B** = ชุดเดียวกับหน้า ติดตาม › Recall — โทรจากหน้านี้ เพราะหน้า Recall เลือกผลการโทรไม่ได้ ผลงานไม่นับ',
    'แท็บ **รอโทร** มีแต่คนที่ยังไม่ได้โทรรากเทียม · แถวที่คนอื่นกำลังติดตามมีป้าย **กำลังติดตาม** · เคยโทรกี่ครั้งดูที่ **เคยโทร N** และประวัติในหน้าต่างคนไข้',
  ],
}

/* ── ข้อ 4 · review before calling ─────────────────────────────────────────── */
export const REVIEW = {
  do: [
    'แท็บ **รีวิว** ฝั่ง **รอรีวิว** → เลือกลิสต์และขั้น (ยังไม่รีวิว / รอตรวจซ้ำ) → คลิกแถว (ไม่จองสาย) → หน้าต่างคนไข้ → ส่วน **รีวิวก่อนโทร** (ลิสต์ W ไม่ต้องรีวิว) · ผ่าน 2 ขั้นแล้วคนไข้ย้ายไปแท็บ **รอโทร** เอง',
    '**1 · คัดกรอง** ดูชาร์ต: ซี่ที่ถอน (ป้าย **ซี่ 8** = ฟันคุด มักไม่ต้องทดแทน) · ทดแทนไปแล้วหรือยัง · มีเหตุที่ไม่ควรเสนอไหม → ผ่าน / ไม่ผ่าน',
    '**2 · ตรวจซ้ำ** ดูอีกรอบ → ผ่าน = **พร้อมโทร** · คนที่มีประสบการณ์ทำทั้ง 2 ขั้นคนเดียวได้',
    'เหตุผลของผล → ช่อง **เหตุผล** ก่อนกดปุ่ม · โน้ตอื่น → ช่อง **บันทึกของผู้รีวิว**',
    'รีวิวนับเฉพาะรอบนี้ — ถอนซี่ใหม่หรือปรึกษาใหม่ต้องรีวิวใหม่ (ของเก่าขึ้นว่า **รอบก่อน**)',
  ],
  warn: 'เรื่องรีวิวเขียนใน **บันทึกของผู้รีวิว** เท่านั้น — โน้ต Recall ในแท็บรีวิวดูได้อย่างเดียว เพราะโน้ต Recall ทุกอันระบบถือว่าโทรแล้ว คนไข้จะหลุดจากลิสต์ B',
  fail: 'ไม่ผ่าน → ย้ายไป **พักจนกว่าจะมีรอบใหม่** — ไม่ต้องโทร',
}

/* ── ข้อ 5 · the call ──────────────────────────────────────────────────────── */
export const CALL = {
  do: [
    'แท็บ **รอโทร** มีแต่คน **พร้อมโทร** → คลิกแถว = จองสาย คนอื่นเห็นป้าย **กำลังติดตาม** · ปิดหน้าต่าง = ปล่อยสาย',
    'แถวที่คนอื่นกำลังติดตามค้างนาน → ถามเจ้าของก่อน แล้วกด **รับต่อ**',
    'โทรผ่าน **Yalecom** — **ประวัติการโทร** ในหน้าต่างอัปเดตเองหลังวางสาย มักภายใน 1 นาที (บางครั้ง 2–3 นาที) ไม่ต้องปิดเปิดหน้าต่าง',
    '**ยืนยันว่าคุยกับเจ้าตัว** ก่อนพูดเรื่องการรักษา',
    '**เลือกผลการโทร 1 ปุ่ม** + เขียนสั้นๆ → บันทึกในโน้ต Recall — **โน้ตที่ไม่เลือกผลไม่นับเป็นการโทร**',
    'ยังไม่มีสายของวันนี้ → ระบบเตือน **ยังไม่พบประวัติการโทรวันนี้** · เพิ่งวางสาย = กด **รอก่อน** แล้วบันทึกใหม่ · โทรจากเบอร์อื่นจริง = **บันทึกต่อ**',
    'บันทึกแล้ว ผลขึ้นในแท็บ **ผลการโทร** และคนไข้ออกจากรอโทรตามผลที่เลือก (ด้านล่าง)',
  ],
  branches: [
    {
      key: 'unreached', tone: 'slate', title: 'ไม่รับสาย', codes: ['NA'],
      do: ['ไม่ต้องจดไว้โทรเอง — ระบบส่งกลับมาให้ · ครั้งหน้าลองเวลาอื่นของวัน', 'เบอร์เสีย → หาเบอร์อื่นในข้อมูลคนไข้'],
      result: 'หายจากรอโทร 3 วัน แล้วกลับมาเป็น **โทรซ้ำ**',
    },
    {
      key: 'notnow', tone: 'amber', title: 'ติดต่อได้ ยังไม่พร้อม', codes: ['CN', 'PR', 'FE'],
      do: [
        'ย้ายไป **พักไว้ 6 เดือน** แล้วกลับมาเป็น พร้อมโทร เอง',
        'ติดเรื่องราคา → เสนอทางเลือก **ฟันปลอม** ได้',
        'มีเหตุให้โทรก่อน เช่น โปรใหม่ → **ดึงกลับมาโทรตอนนี้** + เหตุผล',
      ],
    },
    {
      key: 'out', tone: 'sky', title: 'พักจนถอนฟันซี่ใหม่', codes: ['OC', 'NO'],
      do: [
        'ย้ายไป **พักจนกว่าจะมีรอบใหม่** — กลับมาเมื่อถอนฟันซี่ใหม่ หรือมีนัดปรึกษารากเทียมใหม่ · recall ปกติยังโทรได้',
        'ไปทำที่อื่นแล้ว → ถาม **ที่ไหน ซี่ไหน เมื่อไร ราคาเท่าไร** ใส่ในโน้ต',
        'เลือกผิด → **ดึงกลับมาโทรตอนนี้** (เหตุผล: กดผิด)',
      ],
    },
    {
      key: 'cancel', tone: 'rose', title: 'ยกเลิกเคส', codes: ['DNC'],
      do: [
        'ระบบ **ถามยืนยันก่อน** แล้วเปลี่ยนสถานะคนไข้เป็น **ยกเลิกเคส** — ไม่ขึ้นใน recall ทุกประเภทอีก ทั้ง recall ปกติและรากเทียม',
        'กดผิด → เปิดหน้าเต็ม เปลี่ยนสถานะกลับ — **ลบโน้ตไม่ได้คืนสถานะ**',
      ],
    },
    {
      key: 'booked', tone: 'emerald', title: 'จองแล้ว', codes: ['BK'],
      do: ['**ลงนัดในระบบทันที** ด้วยประเภทนัดรากเทียม + รายละเอียดนัดหมาย'],
      warn: 'ไม่ลงนัด = แถวในรอโทรขึ้น ⚠ จองแล้ว · ไม่พบนัด และไม่นับเป็น นัดได้',
      to: 'hold',
    },
  ],
  notes: [
    'คนไข้บอกว่า **ทำที่นี่แล้ว** → ดูชาร์ตว่าเป็นซี่ที่ถอนล่าสุดไหม · ซี่เก่า = คุยเรื่องซี่ใหม่ต่อ · ซี่เดียวกันแต่ไม่มีบิล = อย่าเลือก ไปทำที่อื่นแล้ว · แจ้ง HN ผู้ดูแลระบบ',
    'คนไข้ **ขอไม่ให้ติดต่ออีก** → เลือกผลการโทร **ยกเลิกเคส** (ระบบเปลี่ยนสถานะให้) — หยุดทั้ง recall ปกติและรากเทียม',
  ],
}

/* ── ข้อ 6 · booked, waiting for the day ───────────────────────────────────── */
// report.md §6e "Which appointments put a patient on hold"; `ids` must equal the keys of
// lib/implant.js HOLD_TYPE_LABEL (api 678) — the test checks it. Labels are type · detail
// as the booking form shows them. `main` = the one to use when unsure.
export const HOLD_TYPES = [
  { ids: [35], label: 'Implant · ปรึกษา', main: true },
  { ids: [83], label: 'Implant · ปรึกษา ส่วนศัลยกรรม' },
  { ids: [84], label: 'Implant · ปรึกษา ส่วน Prosth' },
  { ids: [44], label: 'Implant · ส่วนรากเทียม' },
  { ids: [45], label: 'Implant · ส่วนครอบฟัน' },
  { ids: [70], label: 'Implant · อื่นๆ' },
  { ids: [26, 75], label: 'Surg / Surg SPEC · ผ่าตัดปลูกกระดูกสำหรับฝังรากเทียม' },
  { ids: [21, 74], label: 'Surg / Surg SPEC · ผ่าตัดยกพื้นไซนัส' },
  { ids: [37], label: 'Prosth · ฟันปลอม' },
  { ids: [40], label: 'Prosth · สะพานฟัน' },
]
export const HOLD_MAIN_HINT = 'ไม่แน่ใจ ใช้ Implant · ปรึกษา'
export const HOLD_NOT = 'ไม่นับ: Prosth · ครอบฟัน · Prosth · เดือยฟัน · Prosth / Surg · อื่นๆ · Surg · ปรึกษา (ส่วนใหญ่ฟันคุด) · นัด GP ทุกแบบ'

export const HOLD = {
  do: [
    'ลงนัดประเภทรากเทียมแล้ว → คนไข้ย้ายไป **รอวันนัด** เอง ไม่อยู่ในลิสต์โทร · แถวแสดงวันนัดและประเภท',
    'นัดประเภทอื่น (เช่น ขูดหินปูน) → อยู่ลิสต์ A — คุยตอนเขามา',
    'มาแล้วนัดขั้นต่อไป (ผ่าตัด / ครอบฟัน) → รอวันนัดต่อจนถึงนัดถัดไป',
  ],
  warn: 'ลงงานรากเทียมเป็น GP หรือ “อื่นๆ” = ระบบมองไม่เห็น — คนไข้ถูกโทรซ้ำ และผลงานไม่นับ',
}

/* ── ข้อ 7 · the appointment day ───────────────────────────────────────────── */
export const VISIT = {
  do: ['หมอเขียน **รายละเอียดนัดหมาย ทุกนัดปรึกษา**: เสนออะไร ซี่ไหน ราคาเท่าไร — ทีมโทรเห็นในแถว W'],
  branches: [
    {
      key: 'paid', tone: 'emerald', title: 'มา + จ่าย',
      do: ['บิลรากเทียม ฟันปลอม หรือสะพานฟัน — **จ่ายงวดแรกก็นับ**'],
      to: 'done',
    },
    {
      key: 'unpaid', tone: 'amber', title: 'มา แต่ยังไม่จ่าย',
      do: [
        'ไม่ได้นัดขั้นต่อไป → วันรุ่งขึ้นขึ้นลิสต์ **W** ป้าย **ปรึกษาแล้ว ยังไม่จ่าย**',
        'ทีมโทรตามแผนที่หมอเสนอ (วันที่ 21 · 45 · 90)',
      ],
      to: 'call',
    },
    {
      key: 'noshow', tone: 'rose', title: 'ไม่มา / ยกเลิก',
      do: ['วันรุ่งขึ้นกลับขึ้นลิสต์ **บนสุด** ป้าย **นัดหลุด**'],
      to: 'call',
    },
    {
      key: 'graft', tone: 'slate', title: 'จ่ายแค่ปลูกกระดูก / ยกไซนัส',
      do: [
        'ยังไม่มีฟัน — **นัดฝังรากไว้ก่อนคนไข้กลับ**',
        'ไม่มีนัด → ซ่อน 3 เดือนนับจากบิล แล้วกลับเข้าลิสต์ ป้าย **ปลูกกระดูกแล้ว รอฝังราก**',
      ],
    },
  ],
}

/* ── ข้อ 8 · the round ends on a bill ──────────────────────────────────────── */
export const DONE = {
  closes: [
    '**รากเทียม** — โปรโมชั่นรากเทียมทุกยี่ห้อ หรือบิล Implant ที่ใส่ราคา',
    '**ฟันปลอม**',
    '**สะพานฟัน** (ติดแน่น หรือถอดได้)',
  ],
  notClose: 'Stent · ปลูกกระดูก · ยกไซนัส · CT · Digital Guide — ยังไม่มีฟัน',
  notes: [
    'นับเฉพาะบิลตั้งแต่วันเริ่มรอบ (ถอนซี่ล่าสุด หรือวันปรึกษา) — บิลเก่าไม่ปิดรอบใหม่',
    'ลิสต์ E (ใส่ฟันปลอมอยู่แล้ว): จบรอบด้วย **รากเทียมเท่านั้น**',
  ],
}

/* ── ข้อ 9 · the next round — how each ending comes back ───────────────────── */
export const NEXT_ROUND = {
  do: ['**ถอนฟันซี่ใหม่ที่คลินิก** หรือ **มีนัด Implant · ปรึกษาใหม่** = เริ่มรอบใหม่ → กลับไปข้อ 1'],
  returns: [
    { ended: 'ซื้อรากเทียม ฟันปลอม สะพานฟัน', back: 'ถอนซี่ใหม่หลังบิลนั้น' },
    { ended: 'ยังไม่พร้อม (พักไว้)', back: 'ครบ 6 เดือน → พร้อมโทร ไม่ต้องรีวิว · ป้าย **กลับจากพัก** (Warm Case ที่ครบ 6 เดือนแล้ว กลับมาใน ปรึกษาเกิน 6 เดือน)' },
    { ended: 'ปรึกษาแล้ว ยังไม่ตัดสินใจ', back: 'เดือน 0–6 Warm Case → เดือน 6–12 ปรึกษาเกิน 6 เดือน → หลัง 1 ปีหยุด จนถอนซี่ใหม่หรือปรึกษาใหม่' },
    { ended: 'ไม่ผ่านรีวิว · เคสไม่เหมาะ · ไปทำที่อื่นแล้ว', back: 'รอบใหม่ → **รีวิวใหม่**' },
    { ended: 'จ่ายปลูกกระดูก / ยกไซนัส', back: '3 เดือนหลังบิล · ป้าย **ปลูกกระดูกแล้ว รอฝังราก**' },
    { ended: 'ไม่รับสาย', back: 'ครบ 3 วัน → กลับรอโทร · ป้าย **โทรซ้ำ**' },
    { ended: 'ยกเลิกเคส', back: 'ไม่กลับมาเอง' },
    { ended: 'ดึงกลับมาโทรตอนนี้', back: 'ทันที → พร้อมโทร · ป้าย **ดึงกลับมาโทร**' },
  ],
}

/* ── how results are counted (api 680, the ผลการโทร log book) — the guide's panel
 * ระบบนับผลงานอย่างไร. Same rules as the tab's four tiles. ──────────────────────── */
export const COUNTING = [
  'แท็บ **ผลการโทร** = สมุดบันทึกของแท็บรอโทร: คนไข้ 1 แถว ผลล่าสุดขึ้นก่อน ผลครั้งก่อนเป็นป้ายเล็กใต้ผลล่าสุด · ทุกครั้งอยู่ในโน้ต Recall ของหน้าต่างคนไข้ (เปิดดูได้อย่างเดียว)',
  '**โทรแล้ว** = โน้ต Recall ที่เลือกผลการโทร — ทุกประเภท **รวม Warm Case** · **รับสาย** = ทุกผลยกเว้น ไม่รับสาย',
  '**นัดได้** = นัดประเภทรากเทียม ไม่ยกเลิก ลงภายใน 14 วันหลังโทร · เลือก **จองแล้ว** นับนัดที่ลงไว้ก่อนโทรไม่เกิน 14 วันด้วย',
  '**จ่ายแล้ว** = บิลจบรอบ (รากเทียม ฟันปลอม สะพานฟัน) ที่จ่ายตั้งแต่วันที่โทรครั้งแรกในช่วงที่เลือก — ตัวเลขที่สำคัญที่สุด',
  'กราฟดาว **ผลล่าสุดของคนที่โทร** เปลี่ยนตามช่วงวันที่ แต่ไม่เปลี่ยนตามตัวกรอง ผลการโทร · กดจุดบนกราฟเพื่อดูรายชื่อ',
]

/* ── the map ───────────────────────────────────────────────────────────────────
 * The words on the drawn flowchart (Ram, 2026-10-29: "look similar to the image… make
 * it vertical" — the journey map in implant/recall-journey.html, turned top to bottom).
 * Where each box sits is WorkflowGuide.jsx's job; this is only what it says.
 *
 * A box opens `step` in the detail drawer and, with `branch`, scrolls to that branch
 * (a DOORS key, a CALL / VISIT branch key, or in / views / out for `who`). `tone` puts
 * a coloured dot before the title: where the patient ends up, not who did it. Boxes do
 * not wrap — keep a line under ~26 characters.
 */
export const MAP_BOXES = {
  lead:   { step: 'doors', branch: 'lead', title: 'คนไข้ใหม่ ทักเข้ามา', lines: ['Chat · โทรเข้า', 'ยังไม่นัด → Ticket ให้ Sale'] },
  chair:  { step: 'doors', branch: 'chair', title: 'คุยกันที่สาขา', lines: ['แนะนำทดแทนฟันทุกเคส', 'สนใจ → นัดก่อนกลับ'] },
  ext:    { step: 'doors', branch: 'ext', title: 'ถอนฟันที่คลินิก', lines: ['เช้าวันรุ่งขึ้น 07:05', 'ระบบใส่ลิสต์ให้เอง'] },
  who:    { step: 'who', title: 'ใครอยู่ในลิสต์', lines: ['ระบบคัดให้ทุกเช้า'] },
  out:    { step: 'who', branch: 'out', tone: 'rose', title: 'ไม่เข้าลิสต์', lines: ['ฟันคุด · สปสช. · ยกเลิกเคส'] },
  pick:   { step: 'pick', title: 'เลือกลิสต์', lines: ['W → A → G → C → E → D → F'] },
  review: { step: 'review', title: 'รีวิวก่อนโทร', lines: ['ขั้น 1 คัดกรอง', 'ขั้น 2 ตรวจซ้ำ'] },
  stop:   { step: 'call', branch: 'out', tone: 'sky', title: 'พักจนกว่าจะมีรอบใหม่', lines: ['ไม่ต้องโทรในรอบนี้'] },
  call:   { step: 'call', title: 'โทร', lines: ['จองสาย → ยืนยันตัวตน', 'เลือกผลการโทรทุกสาย'] },
  park:   { step: 'call', branch: 'notnow', tone: 'amber', title: 'พักไว้ 6 เดือน', lines: ['ยังไม่พร้อม'] },
  hold:   { step: 'hold', title: 'รอวันนัด', lines: ['นัดประเภทรากเทียม · ไม่ต้องโทร'] },
  visit:  { step: 'visit', title: 'วันนัด', lines: ['หมอเขียนรายละเอียดนัดหมาย'] },
  noshow: { step: 'visit', branch: 'noshow', tone: 'rose', title: 'นัดหลุด', lines: ['ไม่มา / ยกเลิก · วันรุ่งขึ้น'] },
  unpaid: { step: 'visit', branch: 'unpaid', tone: 'amber', title: 'ปรึกษาแล้ว ยังไม่จ่าย', lines: ['ไม่มีนัดต่อ · วันรุ่งขึ้น → W'] },
  graft:  { step: 'visit', branch: 'graft', tone: 'slate', title: 'ปลูกกระดูก / ยกไซนัส', lines: ['ไม่มีนัดฝังราก · ครบ 3 เดือน'] },
  done:   { step: 'done', tone: 'emerald', title: 'จบรอบ', lines: ['รากเทียม · ฟันปลอม · สะพานฟัน'] },
  next:   { step: 'next', title: 'รอบใหม่', lines: ['ถอนซี่ใหม่ หรือ ปรึกษาใหม่'] },
}

/** What travels down each arrow. */
export const MAP_LABELS = {
  booked: 'นัดได้ → ลงนัดรากเทียม',
  skip: 'W ไม่ต้องรีวิว',
  ready: 'พร้อมโทร',
  fail: 'ไม่ผ่าน',
  notOurs: 'ไม่ใช่เคสเรา',
  retry: 'ไม่รับสาย → 3 วันโทรซ้ำ',
  sixMonths: 'ครบ 6 เดือน',
  bk: 'จองแล้ว',
  nextStage: 'นัดขั้นต่อไป',
  day: 'ถึงวันนัด',
  back: 'กลับเข้าลิสต์',
  paid: 'มา + จ่าย',
  round: 'เริ่มรอบใหม่ → ข้อ 1',
}

export const MAP_LEGEND = {
  go: 'ไปขั้นต่อไป',
  back: 'วนกลับมาโทร',
  badge: 'ข้อในคู่มือ',
  roles: 'จุดสี = ใครทำ',
  hint: 'กดที่กล่องเพื่อดูว่าต้องทำอะไร และทำไมต้องทำแบบนั้น',
}

/* ── the three rules that cost the most when broken ────────────────────────── */
export const MUST = [
  'งานรากเทียมลงนัดด้วย **ประเภทนัดรากเทียม** + เขียน **รายละเอียดนัดหมาย** เสมอ',
  '**เลือกผลการโทร** ทุกสาย แม้โทรไม่ติด',
  '**นัดขั้นต่อไปก่อนคนไข้กลับ** ทุกครั้ง',
]
export const NEVER = [
  'เปลี่ยนสถานะคนไข้เป็น “รากเทียม” เพื่อติดธง — สถานะมีได้ค่าเดียว คนไข้จะหายจากลิสต์ของแผนกอื่น (ลิสต์ B คือคนที่ถูกตั้งไว้ก่อนแล้ว)',
  'พิมพ์เรื่องรีวิวลงโน้ต Recall',
  'ทำรายชื่อแยกใน Excel หรือ LINE — ทำงานจากหน้านี้เท่านั้น',
]
